import { Genre } from '@usheru-hff/shared/domain/genre';
import { isStringArray, StringKeys } from '@usheru-hff/shared/utils';

import { Crew } from '../crew-entities';
import { Language } from '../language.model';
import { Tag } from '../tag.model';
import { Video } from '../video-entities';
import { MovieEpisode } from './movie-episode.model';
import { MovieFestival } from './movie-festival.model';
import { MovieLaurel } from './movie-laurel.model';
import { MoviePress } from './movie-press.model';
import { SocialNetworkLink } from './social-network-link.model';
import { Company } from '../company.model';
import { Image } from '../image-entities/image.model';
import { Country } from '../location-entities/country.model';
import { LocationCertification } from '../location-entities/location-certification.model';
import { News } from '../news.model';

export interface Movie {
  id: string;
  /**
   * 1 - Movie
   *
   * 2 - Event
   *
   * 3 - TV/Series
   **/
  contentType: number;
  trTitle: string;
  friendlyUrl: string;
  description: string;

  /**
   * dd-mm-yyyy
   */
  releaseDate: string;

  /**
   * dd-mm-yyyy
   */
  streamingReleaseDate: string;
  locStreamingReleaseDate: string;
  runtime: number;
  officialSite?: string;
  advisory?: string;
  isThesisMovie?: boolean;
  imdb: string;
  tmdb: string;
  rottentomato?: string;
  letterboxd?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rentraks: any[];
  country: string;
  language: string;
  tilePhoto?: string;
  tilePhotoUrl?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  distributionCompaniesMap: any;
  logo?: string;
  logoUrl?: string;
  portraitPhoto?: string;
  portraitPhotoUrl?: string;
  fullVideoUrl?: string;
  fullpagePhoto: string;
  fullpageImageUrl: string;
  // movies/v2/[type] will return string[]
  tags?: Tag[] | string[];
  trailerLink: string;
  trailerUrl?: string;
  trTrailerLink: string;
  trTrailerUrl?: string;
  // movies/v2/[type] will return string[]
  genres?: Genre[];
  certification?: string;
  countries?: Country[];
  languages?: Language[];
  title?: string;
  trLogo?: string;
  /**
   * @deprecated use `description` instead
   */
  trDescription?: string;
  trReasonsToWatch?: string;
  trPortraitPhoto?: string;
  locCertification?: LocationCertification;
  locAdvisory?: string;
  locRuntime?: number;
  /**
   * dd-mm-yyyy
   */
  locReleaseDate?: string;
  locCinemaEnd?: number;
  locStreamingAvailable?: boolean;
  movieImages?: Image[];
  moviePosters?: Image[];
  movieVideos?: Video[];
  movieCrew?: Crew[];
  movieFestivals?: MovieFestival[];
  movieLaurels?: MovieLaurel[];
  socialNetworks?: SocialNetworkLink[];
  favicon?: string;
  faviconImageUrl?: string;
  copyrightImage?: string;
  copyrightImageUrl?: string;
  movieStatus?: number;
  releaseMessage?: string;
  contractualPartners?: Company[];
  customMovieCrew?: string;
  producingCompanies?: Company[];
  positionOrder?: number;
  movieRecommendations?: Movie[];
  reviews?: MoviePress[];
  episodes?: MovieEpisode[];
  salesAgents?: Company[];
  hasPlayer?: boolean;
  pressKit?: string;
  hasAssets?: boolean;
  userRating?: number;
  channelRating?: number;
  watched?: number;
  bookmarks?: number;
  disableShowtimes?: boolean;
  defaultStreaming?: boolean;
  disableStreaming?: boolean;
  globalInCinemas?: boolean;
  globalInStreaming?: boolean;
  /**
   * @depreacted use inMyWishlist instead
   * */
  inMyWhislist?: boolean;
  //v2 corrected the property name
  inMyWishlist?: boolean;
  inMyWatched?: boolean;
  myRank?: number;
  newsPosts?: News[];
  /**
   * @deprecated
   * Used in the development. Can be removed in the future after set the chSettings in the API
   */
  brandedMovie?: {
    status: 'awareness' | 'conversion';
    brandColor: string;
    backgroundColor: string;
    fontFamily: string;
    fontFamilyBold: string;
  };
  /**
   * This value comes as string from the API
   * Value parsed from request with the {@link parseMovieChSettings} function
   */
  settings: {
    selectType?: number;
    selectShowtimeFlow?: boolean;
    movieSiteMode?: 'AWARENESS' | 'CONVERSION';
    selectShowtimeCinemas?: number;
    selectShowtimeBoldFont?: string;
    selectShowtimeLocation?: string;
    movieSiteBoldFont?: string;
    movieSiteEnabled?: boolean;
    selectShowtimeCountries?: string[];
    selectShowtimeBrandColor?: string;
    selectShowtimeNormalFont?: string;
    movieSiteBrandColor?: string;
    movieSiteNormalFont?: string;
    movieSiteBoldFontFile?: string;
    movieSiteNormalFontFile?: string;
    selectShowtimeBackgroundColor?: string;
    movieSiteAutomatedSwitch?: boolean;
    movieSiteBackgroundColor?: string;
    selectShowtimeElementBorderRadius?: string;
    selectShowtimeContainerBorderRadius?: string;
  };
}

// v2 API ruses`inMyWishlist` and v1 uses `inMyWhislist`
export const isInMyWishlist = (movie: Movie): boolean => movie.inMyWishlist ?? movie.inMyWhislist ?? false;

// v2 API rest calls return string[]
export const getMovieTags = (movie: Movie | undefined, tagProperty: StringKeys<Tag> = 'friendlyUrl'): string[] =>
  !movie || !movie?.tags ? [] : isStringArray(movie.tags) ? movie.tags : movie.tags?.map(tag => tag[tagProperty]);

export const existsMovieTag = (
  movie: Movie | undefined,
  tagProperty: StringKeys<Tag> = 'friendlyUrl',
  value: string
): boolean => {
  // 1. If movie is undefined or tags doesn't exist, return false
  if (!movie?.tags) {
    return false;
  }

  // 2. If tags is an array of strings, check directly
  if (isStringArray(movie.tags)) {
    return movie.tags.some(tag => tag === value);
  }

  // 3. Otherwise, tags is an array of Tag objects
  return movie.tags.some((tag: Tag) => tag[tagProperty] === value);
};

// v2 API rest calls return string[]
export const getMovieGenres = (movie: Movie | undefined, tagProperty: StringKeys<Genre> = 'slug'): string[] => {
  // If there's no genres array, return empty
  if (!movie?.genres) {
    return [];
  }

  // If genres is an array of string
  if (isStringArray(movie.genres)) {
    return movie.genres;
  }

  // Otherwise, map over the tags array of Genre objects
  return movie.genres.map(tag => tag[tagProperty]);
};
export const isMovie = (obj: object): boolean => {
  try {
    return 'friendlyUrl' in obj && 'title' in obj && 'trailerUrl' in obj && 'genres' in obj;
  } catch {
    return false;
  }
};

export const posGetMovieFromApi = (movie: Movie): Movie => parseMovieChSettings(movie);

export const parseMovieChSettings = (movie: Movie): Movie => {
  if (movie.settings && typeof movie.settings === 'string') {
    movie.settings = JSON.parse(movie.settings);

    // Replace the 'true' or 'false' string to boolean

    if (typeof movie.settings.movieSiteEnabled === 'string') {
      movie.settings.movieSiteEnabled = (movie.settings?.movieSiteEnabled as any) === 'true';
    }

    if (typeof movie.settings.movieSiteAutomatedSwitch === 'string') {
      movie.settings.movieSiteAutomatedSwitch = (movie.settings?.movieSiteAutomatedSwitch as any) === 'true';
    }
  }

  return movie;
};
